import type { MaybeRefOrGetter } from "vue";
import type { ParticipantPage } from "~/types/participantStatus";
import {
  PARTICIPANT_HEARTBEAT_INTERVAL_MS,
  PARTICIPANT_IDLE_AFTER_SECONDS,
} from "~/utils/participantStatus";

/** Input that means the participant is still there. */
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart", "wheel"] as const;

const readCookie = (name: string): string | null => {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

/**
 * Keeps the notary's status badge for this participant current: the page
 * checks in every half minute, and at once whenever it is hidden or shown,
 * comes back online, moves to another page, or the participant does something
 * after being idle. Each check-in says which page they are on, whether it is in
 * view and how long they have been still; the API works out the rest.
 *
 * Nothing about the session depends on this, so a check-in that fails is
 * dropped: the next one will say the same thing.
 */
export const useParticipantHeartbeat = (
  participantId: MaybeRefOrGetter<string | null | undefined>,
  page: MaybeRefOrGetter<ParticipantPage | null | undefined>,
  /** More for the check-in to report, such as how the page is connected. */
  extras: () => Record<string, unknown> = () => ({}),
) => {
  const { $axios } = useNuxtApp();
  const config = useRuntimeConfig();

  let lastInputAt = Date.now();

  const idleSeconds = () => Math.floor((Date.now() - lastInputAt) / 1000);

  const body = (visible = document.visibilityState === "visible") => ({
    ...extras(),
    page: toValue(page),
    visible,
    idle_seconds: idleSeconds(),
  });

  const uri = () => `/v1/model_has_sessions/${toValue(participantId)}/heartbeat`;

  const canBeat = () => Boolean(toValue(participantId) && toValue(page));

  const beat = () => {
    if (!canBeat()) {
      return;
    }
    $axios.post(uri(), body()).catch(() => {
      // A missed check-in is caught up by the next one.
    });
  };

  /**
   * The last word as the page goes away. A normal request is cancelled with the
   * page, so this one is sent with keepalive, which outlives it; it cannot go
   * through axios, so it carries axios' headers itself.
   */
  const farewell = () => {
    if (!canBeat()) {
      return;
    }
    const xsrf = readCookie("XSRF-TOKEN");
    fetch(`${config.public.apiBase}${uri()}`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Tenant": window.location.hostname.split(".")[0] ?? "app",
        ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
      },
      body: JSON.stringify(body(false)),
    }).catch(() => {});
  };

  const onInput = () => {
    const wasIdle = idleSeconds() >= PARTICIPANT_IDLE_AFTER_SECONDS;
    lastInputAt = Date.now();
    if (wasIdle) {
      beat();
    }
  };

  for (const event of ACTIVITY_EVENTS) {
    useEventListener(window, event, onInput, { passive: true });
  }
  useEventListener(document, "visibilitychange", beat);
  useEventListener(window, "online", beat);
  useEventListener(window, "pagehide", farewell);

  const { pause } = useIntervalFn(beat, PARTICIPANT_HEARTBEAT_INTERVAL_MS);

  watch(
    () => [toValue(participantId), toValue(page)],
    () => beat(),
    { immediate: true },
  );

  onScopeDispose(pause);

  return { beat };
};
